'use client'

import { createContext, useContext, useEffect, useState, useRef, useMemo, type ReactNode } from 'react'
import { isPortalSurfaceClient } from '@/lib/portal-surface-client'
import { normalizeShopDomain } from '@/lib/shop-domain'
import type { BootstrapData } from '@/app/(app)/context/BootstrapContext'
import { portalSessionBootstrapToData } from '@/lib/portal/portalSessionBootstrapMap'

export interface BootState {
  bootHost: string | null
  bootShop: string | null
  bootReady: boolean
  /**
   * Portal: `loading` until the first `/api/portal/session?hydrate=1` completes.
   * Embedded apps use `done` (unused).
   */
  portalSessionStatus: 'loading' | 'done'
  /** Portal: bootstrap payload from hydrated session when the server resolved a shop. */
  portalSessionBootstrap: BootstrapData | null
  /** Shop domain the `portalSessionBootstrap` was built for (matches server effective shop). */
  portalBootstrapShop: string | null
}

const BootContext = createContext<BootState | null>(null)

function readShopHostFromWindow(): { shop: string | null; host: string | null } {
  if (typeof window === 'undefined') return { shop: null, host: null }
  const params = new URLSearchParams(window.location.search)
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const qsHost = params.get('host') || hashParams.get('host')
  const qsShop = params.get('shop') || hashParams.get('shop')
  const savedHost = window.localStorage.getItem('shopify-host')
  const savedShop = window.localStorage.getItem('shopify-shop')
  return {
    host: qsHost || savedHost,
    shop: qsShop || savedShop,
  }
}

function initialBootShop(): string | null {
  if (typeof window === 'undefined') return null
  const { shop } = readShopHostFromWindow()
  return shop ? normalizeShopDomain(shop) : null
}

function initialBootHost(): string | null {
  if (typeof window === 'undefined') return null
  if (isPortalSurfaceClient()) return null
  return readShopHostFromWindow().host
}

export function BootProvider({ children }: { children: ReactNode }) {
  const [bootHost, setBootHost] = useState<string | null>(initialBootHost)
  /** URL + localStorage (shopify-shop); portal uses same hint so shell is instant after login / set-shop. */
  const [bootShop, setBootShop] = useState<string | null>(initialBootShop)
  /**
   * Client: ready immediately — do not block the shell on /api/portal/session.
   * Portal session effect reconciles shop and clears on 401.
   */
  const [bootReady, setBootReady] = useState(() => typeof window !== 'undefined')
  const [portalSessionStatus, setPortalSessionStatus] = useState<'loading' | 'done'>(() =>
    typeof window !== 'undefined' && isPortalSurfaceClient() ? 'loading' : 'done'
  )
  const [portalSessionBootstrap, setPortalSessionBootstrap] = useState<BootstrapData | null>(null)
  const [portalBootstrapShop, setPortalBootstrapShop] = useState<string | null>(null)
  const ranRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (isPortalSurfaceClient()) {
      let cancelled = false
      void (async () => {
        try {
          const hintRaw = window.localStorage.getItem('shopify-shop')
          const hint = hintRaw ? normalizeShopDomain(hintRaw) : ''
          const sessionUrl = new URL(`${window.location.origin}/api/portal/session`)
          sessionUrl.searchParams.set('hydrate', '1')
          if (hint) sessionUrl.searchParams.set('hintShop', hint)
          const res = await fetch(sessionUrl.toString(), { credentials: 'include' })
          if (cancelled) return
          if (res.ok) {
            const data = (await res.json()) as {
              selectedShop?: string | null
              shops?: { shopId: string }[]
              bootstrap?: unknown
              bootstrapShop?: string | null
            }
            const allowedIds = (data.shops ?? [])
              .map((s) => normalizeShopDomain(s.shopId))
              .filter(Boolean)
            const allowed = new Set(allowedIds)

            let resolved: string | null = null
            const sel = data.selectedShop ? normalizeShopDomain(data.selectedShop) : null
            if (sel && allowed.has(sel)) resolved = sel
            else if (allowed.size === 1) resolved = allowedIds[0] ?? null
            else {
              const fromStorage = window.localStorage.getItem('shopify-shop')
              const hintStored = fromStorage ? normalizeShopDomain(fromStorage) : null
              if (hintStored && allowed.has(hintStored)) resolved = hintStored
            }

            const mapped = portalSessionBootstrapToData(data.bootstrap)
            const serverBootstrapShop = data.bootstrapShop ? normalizeShopDomain(data.bootstrapShop) : null
            if (mapped && serverBootstrapShop && allowed.has(serverBootstrapShop)) {
              setPortalSessionBootstrap(mapped)
              setPortalBootstrapShop(serverBootstrapShop)
            } else {
              setPortalSessionBootstrap(null)
              setPortalBootstrapShop(null)
            }

            if (resolved) {
              window.localStorage.setItem('shopify-shop', resolved)
              window.localStorage.removeItem('shopify-host')
              setBootShop(resolved)
              setBootHost(null)
            } else {
              setBootShop(null)
              setBootHost(null)
            }
          } else {
            setBootShop(null)
            setBootHost(null)
            setPortalSessionBootstrap(null)
            setPortalBootstrapShop(null)
          }
        } catch {
          if (!cancelled) {
            setBootShop(null)
            setBootHost(null)
            setPortalSessionBootstrap(null)
            setPortalBootstrapShop(null)
          }
        } finally {
          if (!cancelled) setPortalSessionStatus('done')
        }
      })()
      return () => {
        cancelled = true
      }
    }

    if (ranRef.current) return
    ranRef.current = true

    const { host, shop } = readShopHostFromWindow()

    if (host) window.localStorage.setItem('shopify-host', host)
    if (shop) window.localStorage.setItem('shopify-shop', shop)
    if (shop && !host) window.localStorage.removeItem('shopify-host')

    setBootHost(host)
    setBootShop(shop)
    setBootReady(true)
  }, [])

  const value = useMemo(
    () => ({
      bootHost,
      bootShop,
      bootReady,
      portalSessionStatus,
      portalSessionBootstrap,
      portalBootstrapShop,
    }),
    [bootHost, bootShop, bootReady, portalSessionStatus, portalSessionBootstrap, portalBootstrapShop]
  )

  return (
    <BootContext.Provider value={value}>
      {children}
    </BootContext.Provider>
  )
}

export function useBoot(): BootState {
  const ctx = useContext(BootContext)
  if (!ctx) {
    return {
      bootHost: null,
      bootShop: null,
      bootReady: false,
      portalSessionStatus: 'done',
      portalSessionBootstrap: null,
      portalBootstrapShop: null,
    }
  }
  return ctx
}
